import React from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-th-bg text-th-text">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-th-muted hover:text-th-text mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-th-muted mb-8">Last updated: April 1, 2026</p>

        <div className="space-y-6 text-sm text-th-text-sec leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Overview</h2>
            <p>
              Pinned ("we", "our", "us") is a travel discovery app operated by Andres Jaramillo.
              This policy explains what data we collect, how we use it, who we share it with, and your rights.
              By creating an account, you consent to the collection and processing of your data as described
              in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Data Controller</h2>
            <p>
              The data controller responsible for your personal data is:
            </p>
            <p className="mt-2">
              <strong>Andres Jaramillo</strong><br />
              Email: <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a><br />
              Pinned is operated from the United States (Florida).
            </p>
            <p className="mt-2">
              For users in Colombia, the data controller also acts as the Responsable del Tratamiento and
              Encargado del Tratamiento under Ley 1581 de 2012.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Data We Collect</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account info:</strong> email address, username, first and last name, and password (stored securely using one-way hashing — never in plain text).</li>
              <li><strong>Profile info:</strong> avatar photo, bio, travel styles, countries and cities visited (all optional, provided by you).</li>
              <li><strong>Posts:</strong> photos you upload, captions, category selections, ratings, and cuisine tags.</li>
              <li><strong>Safety records:</strong> reports you submit about content or users, and your list of blocked users. Reports are visible only to us; your block list is visible only to you.</li>
              <li><strong>Location data:</strong> GPS coordinates extracted from photo metadata (EXIF data) when you upload a photo. This location is used to place your pin on the map and is displayed publicly.</li>
              <li><strong>Comments:</strong> text you post on other users' pins.</li>
              <li><strong>Device tokens:</strong> for push notifications, only if you opt in through your device settings.</li>
              <li><strong>Usage data:</strong> post view counts and map impression counts. We do not use third-party analytics trackers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">How We Use Your Data</h2>
            <p>
              The legal basis for processing your data is your explicit consent, provided when you create
              an account and agree to these terms. We process your data for the following purposes:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Provide the service:</strong> display your posts and profile to other users, place pins on the map, and enable comments and interactions.</li>
              <li><strong>Account management:</strong> authenticate your login, send verification codes, and process password resets.</li>
              <li><strong>Notifications:</strong> send push notifications about activity on your posts (comments, view milestones) if you have opted in.</li>
              <li><strong>Transactional emails:</strong> send verification codes and password reset codes via email.</li>
              <li><strong>Stats and credibility:</strong> calculate travel statistics, explorer stats, and cuisine credibility scores displayed on your profile.</li>
              <li><strong>Traveler similarity:</strong> compare your chosen travel styles and your public posting activity (the categories and countries of your pins) with those of other users, to show both of you how similarly you travel. This uses only information that is already public on your profile and posts.</li>
              <li><strong>Improve the service:</strong> understand usage patterns to improve app performance and features.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Data Sharing</h2>
            <p>
              We do not sell, rent, or trade your personal data. We share data with third-party service
              providers solely to operate the App:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Image hosting provider:</strong> stores and optimizes photos you upload.</li>
              <li><strong>Database provider:</strong> securely stores your account data, posts, and comments.</li>
              <li><strong>Email delivery provider:</strong> sends transactional emails (verification codes, password resets).</li>
              <li><strong>App hosting providers:</strong> serve the frontend application and backend API.</li>
            </ul>
            <p className="mt-2">
              These providers process data only on our behalf and are contractually obligated to protect
              your data. We do not share data with advertisers or data brokers.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Location Data</h2>
            <p>
              When you upload a photo, we extract GPS coordinates from the image's EXIF metadata to determine
              where the photo was taken. This location is displayed publicly on the map as a pin. You can
              review the detected location before publishing. If you do not want location data shared, do
              not upload photos that contain GPS metadata, or strip the metadata before uploading.
            </p>
            <p className="mt-2">
              We do not track your real-time location or access your device's GPS sensor.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Data Retention and Deletion</h2>
            <p>
              Your data is retained as long as your account is active. You can delete individual posts at
              any time through the App.
            </p>
            <p className="mt-2">
              You can delete your entire account through the Settings page in the App. Upon account deletion:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Your profile, posts, comments, notifications, view records, and verification codes are permanently deleted within 30 days.</li>
              <li>Encrypted backups containing your data may persist for up to 90 days before being automatically purged.</li>
              <li>Content that was previously shared or cached by third parties (e.g., link previews on social media) may persist beyond your control and ours.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Your Rights</h2>
            <p>
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Access:</strong> request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> update or correct inaccurate data through your profile or by contacting us.</li>
              <li><strong>Deletion:</strong> delete your account and all associated data through the App settings, or request deletion by email.</li>
              <li><strong>Portability:</strong> request an export of your data in a machine-readable format.</li>
              <li><strong>Revoke consent:</strong> withdraw your consent to data processing at any time by deleting your account. For push notifications, disable them through your device settings.</li>
              <li><strong>Complaint:</strong> file a complaint with your local data protection authority.</li>
            </ul>
            <p className="mt-2">
              <strong>For users in Colombia:</strong> you may exercise your rights of access, correction, deletion,
              and revocation of consent as provided by Ley 1581 de 2012, and file complaints with the
              Superintendencia de Industria y Comercio (SIC).
            </p>
            <p className="mt-2">
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a>.
              We will respond within 15 business days (20 calendar days for users in Mexico, per LFPDPPP).
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Children</h2>
            <p>
              Pinned is not intended for anyone under 18 years of age. We do not knowingly collect personal
              data from children under 18. If we become aware that we have collected data from a person under
              18, we will promptly delete the account and all associated data. If you believe someone under
              18 has created an account, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">International Data Transfers</h2>
            <p>
              Your data is processed and stored in the United States. If you are accessing the App from
              outside the United States, your data will be transferred to and processed in the US. By
              creating an account, you consent to this transfer.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Security</h2>
            <p>
              We use industry-standard measures to protect your data, including encrypted connections (HTTPS),
              hashed passwords (bcrypt), and secure database hosting. However, no method of transmission or
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Changes</h2>
            <p>
              We may update this policy from time to time. Changes will be posted on this page with an
              updated date. Material changes will be communicated via email or in-app notification at
              least 14 days before taking effect. Continued use of the App after changes constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-th-text mb-2">Contact</h2>
            <p>
              Questions about this policy or your data? Email us at{" "}
              <a href="mailto:hello@usepinned.com" className="text-th-accent hover:underline">hello@usepinned.com</a>.
            </p>
            <p className="mt-2">
              See also our{" "}
              <Link to="/terms" className="text-th-accent hover:underline">Terms of Service</Link>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
